const STATUS_LABEL = {
    done: '완료',
    skipped: '건너뜀',
    refused: '거부',
    partial: '일부 완료',
    remaining: '미기록 · 확인 필요',
};
function oneLine(value, maximum) {
    const normalized = value.normalize('NFC').replace(/\s+/gu, ' ').trim();
    if (!normalized || normalized.length > maximum)
        throw new RangeError('handoff text is invalid');
    return normalized;
}
/**
 * Builds a user-reviewed factual handoff. It never infers a diagnosis, warning,
 * recipient, or treatment decision and is deterministic for the same input.
 */
export function buildCareHandoff(input) {
    const petName = oneLine(input.petName, 20);
    const createdLabel = oneLine(input.createdLabel, 40);
    const expiresLabel = oneLine(input.expiresLabel, 40);
    if (!/^\d{4}-\d{2}-\d{2}$/u.test(input.localDate))
        throw new RangeError('handoff date is invalid');
    if (input.items.length < 1 || input.items.length > 1000)
        throw new RangeError('handoff item count is invalid');
    const items = [...input.items]
        .map((item) => ({
        ...item,
        id: oneLine(item.id, 120),
        label: oneLine(item.label, 50),
        localTime: oneLine(item.localTime, 20),
        ...(item.actorLabel ? { actorLabel: oneLine(item.actorLabel, 20) } : {}),
        ...(item.detail ? { detail: oneLine(item.detail, 500) } : {}),
    }))
        .sort((left, right) => left.localTime.localeCompare(right.localTime) ||
        left.kind.localeCompare(right.kind) ||
        left.id.localeCompare(right.id));
    const completed = items.filter((item) => item.kind !== 'observation' && item.status !== 'remaining');
    const observations = items.filter((item) => item.kind === 'observation');
    const remaining = items.filter((item) => item.status === 'remaining');
    const itemLine = (item) => `- ${item.localTime} · ${item.label}${item.kind === 'observation' ? ` · ${item.detail ?? ''}` : ` · ${STATUS_LABEL[item.status]}`}${item.actorLabel ? ` · ${item.actorLabel} 기록` : ''}`;
    const note = input.note?.normalize('NFC').trim() ?? '';
    if (note.length > 500)
        throw new RangeError('handoff note is too long');
    const sections = [
        `[펫리듬] ${petName} 돌봄 인계`,
        `${input.localDate} · ${createdLabel} 작성`,
        '',
        '실제 결과가 있는 돌봄',
        ...(completed.length ? completed.map(itemLine) : ['- 선택한 실제 결과 없음']),
        '',
        '결과를 확인할 일정',
        ...(remaining.length ? remaining.map(itemLine) : ['- 선택한 미기록 일정 없음']),
    ];
    if (observations.length)
        sections.push('', '오늘 직접 남긴 관찰', ...observations.map(itemLine));
    if (note)
        sections.push('', '직접 남긴 메모', note);
    sections.push('', `다시 확인할 시각: ${expiresLabel}`, '이 내용은 보호자가 고른 기록과 메모입니다. 진단이나 판단이 아닙니다.');
    return {
        text: sections.join('\n'),
        selectedCount: items.length,
        completedCount: completed.length + observations.length,
        remainingCount: remaining.length,
    };
}
/** Reuses the same saved facts as history; never synthesizes missing observations. */
export function buildObservationHandoffItems(rows, date) {
    const from = Date.parse(`${date}T00:00:00+09:00`);
    return rows.filter((row) => (row.family === 'observation' || row.table === 'meals' || row.table === 'waterLogs') && row.at >= from && row.at < from + 86400000)
        .map((row) => ({ id: row.id, kind: 'observation', label: row.title, detail: row.detail, localTime: ['coughLogs', 'checkins', 'qolRecords', 'waterLogs', 'photoDays', 'dayMarks'].includes(row.table) ? '시각 미기록' : new Date(row.at + 9 * 60 * 60 * 1000).toISOString().slice(11, 16), status: 'done', ...(row.actorLabel ? { actorLabel: row.actorLabel } : {}) }));
}
